import { ENV, API_GATEWAY_URL, CLICKSTREAM_API_PATH, EVAM_AUTH_CHECK_RETRY_COUNT, EVAM_AUTH_CHECK_RETRY_TIME } from "utils/constants";
import { getTxnId, getDeviceId, getItem, setItem } from "utils/localStorage";
import { getStateData } from "analytics/index";
import get from "lodash/get";
import axios from "axios";
import { COUNTRY } from "utils/constants";
import { formatMessage } from "utils/helpers";
import { constructClickStreamEventInfo } from "./helpers/constructClickStreamEventData";
import { find, isEmpty } from "lodash";

/**
 * Constructs properties required for clickStreamEventData
 * @param {string} clickStreamEventName used to construct data/properties associated with clickStreamEventName
 * @param {object} data data associated with analytics event name returned from analyticsConstructor
 */
export const constructClickStreamEventData = (event, data, reduxStore) => {
  // function moved to separate file due to code quality issue
  constructClickStreamEventInfo(event, data, reduxStore);
};

/**
 * @param {object} clickStreamEventData
 * @returns {object} payload required for clickStream API
 */
export const constructClickStreamPayload = (
  clickStreamEventData = {},
  reduxStore = {},
  country
) => {
  return {
    records: [
      {
        value: {
          deviceDetails: {
            deviceId: getDeviceId(),
            deviceType: navigator.userAgent
          },
          userDetails: {
            sessionId: getTxnId(),
            userId: getStateData(
              reduxStore,
              "api.loggedInUserDetails.data.external_id",
              ""
            )
          },
          appDetails: {
            appCountry: country,
            appName: "webfront",
            appEnvironment: ENV
          },
          clickStreamInfo: [clickStreamEventData]
        }
      }
    ]
  };
};

/**
 * Makes request to clickstream endpoint
 * @param {object} clickStreamPayload
 */
export const postClickStreamRequest = clickStreamPayload => {
  console.log("Pushing clickStream data... ", clickStreamPayload);
  const clickStreamUrl = API_GATEWAY_URL + CLICKSTREAM_API_PATH;
  const options = {
    headers: {
      "content-type": "application/vnd.kafka.json.v2+json",
      accept: "application/vnd.kafka.v2+json"
    }
  };
  axios
    .post(clickStreamUrl, clickStreamPayload, options)
    .then(_ => console.log("Clickstream data has been pushed"))
    .catch(err => console.error("Clickstream returned an error: ", err));
};
/**
 * @param {object} clickStreamEventData
 * @returns {object} payload required for clickStream API
 */
export const getGTMtagdata = (
  containerName,
  altContainerName,
  loggedInUserDetails,
  altbuttonText,
  orderRef,
  locale
) => {
  let testId = "";
  if (altContainerName != null) {
    testId = containerName ? `${containerName}` : `${altContainerName}`;
  } else {
    testId = containerName;
  }
  const userId = loggedInUserDetails
    ? get(loggedInUserDetails, "external_id")
    : "";

  let buttonLabel = formatMessage(get(locale, `${altbuttonText?.label}`));
  if (altbuttonText === undefined && locale != null) {
    buttonLabel = formatMessage(
      get(locale, `${altContainerName?.label}`),
      "go_back"
    );
  }
  if (buttonLabel === "") buttonLabel = altbuttonText;
  const gtmtagdata = {
    "data-testid": testId,
    "data-deviceid": getDeviceId(),
    "data-devicetype": navigator.userAgent,
    "data-sessionid": getTxnId(),
    "data-userid": userId,
    "data-appcountry": COUNTRY,
    "data-env": ENV,
    "data-buttontext": buttonLabel,
    "data-appname": "webfront",
    "data-orderref": orderRef
  };
  return JSON.stringify(gtmtagdata);
};

export const getBrowserName = () => {
  const userAgent = window?.navigator?.userAgent;

  const browsers = [
    { name: "Firefox", identifier: "Firefox" },
    { name: "Opera", identifier: "Opera" },
    { name: "Opera", identifier: "OPR" },
    { name: "Edge", identifier: "Edg" },
    { name: "Chrome", identifier: "Chrome" },
    { name: "Safari", identifier: "Safari" },
    { name: "Internet Explorer", identifier: "MSIE" },
    { name: "Internet Explorer", identifier: "Trident" }
  ];

  for (const browser of browsers) {
    if (userAgent.includes(browser.identifier)) {
      return browser.name;
    }
  }

  return "Unknown";
};

export const calculateValidityAndExpiry = (store) => {
  const selectedPlanId = getStateData(
    store,
    "api.initialSelectedAddon.data",
    ""
  );
  const planService = getStateData(
    store,
    "api.planService.data.associatedProductVariants",
    []
  );
  if(isEmpty(selectedPlanId) || isEmpty(planService)) {
    return null
  }
  const {unit="", value=0} = find(planService, { sku: selectedPlanId })?.validityDays;
  
  const today = new Date();
  const expiryDate = new Date(today.setDate(today.getDate() + Number(value)));
  const expiry = expiryDate?.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return { validity: `${value} ${unit}`, expiry };
};

export const userSelectedPlan = (reduxStore) =>
  getStateData(reduxStore, "api.eligiblePlans.data.productVariants", []).find(
    (item) =>
      item.sku === getStateData(reduxStore, "api.initialSelectedAddon.data", "")
  );

  export const validateDOBPushEvent = (dob) => {
    const lastPushedEventDOB = getItem("evam_selected_dob");
    if (!dob) return true;
    if (!lastPushedEventDOB || lastPushedEventDOB !== dob) {
      setItem("evam_selected_dob", dob);
      return false;
    }
    return true;
  };

// export const sendCustomEvamData = (eventName, payload) => {
//   const sendEvent = () => {
//     if (
//       window.EvamSdkObject &&
//       window.EvamSdkObject?.sdkSettings?.authenticated
//     ) {
//       console.log("Sending", eventName, payload);
//       window.EvamSdkObject.sendCustomEvent(eventName, payload);
//       clearInterval(interval);
//     }
//   };
//   const interval = setInterval(sendEvent, 100);
//   setTimeout(() => clearInterval(interval), 5000);
// };

export const sendCustomEvamData = (eventName, payload) => {

  if (getItem("effEvamEvents") === "false") {
    return;
  }
  const eventQueue = [];
  eventQueue.push({ eventName, payload });

  // Function to send queued events once SDK is authenticated
  const sendQueuedEvents = () => {
    if (
      window.EvamSdkObject &&
      window.EvamSdkObject?.sdkSettings?.authenticated
    ) {
      while (eventQueue.length > 0) {
        const { eventName, payload } = eventQueue.shift();
        window.EvamSdkObject.sendCustomEvent(eventName, payload);
      }
    }
  };

  // Check if SDK is already authenticated
  if (window.EvamSdkObject?.sdkSettings?.authenticated) {
    sendQueuedEvents();
    return;
  }

  // If not authenticated, check periodically until authentication is successful
  let retryCount = 0;
  const interval = setInterval(() => {
    
    if (window.EvamSdkObject?.sdkSettings?.authenticated) {
      sendQueuedEvents();
      clearInterval(interval);
      return;
    }

    retryCount += 1;
    if (retryCount >= EVAM_AUTH_CHECK_RETRY_COUNT) {
      console.log(`Evam Authentication check failed ${EVAM_AUTH_CHECK_RETRY_COUNT} times. Stopping.`);
      clearInterval(interval);
    }
  }, EVAM_AUTH_CHECK_RETRY_TIME);
};